import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, NEVER, Observable, ReplaySubject, Subject, throwError, timer } from 'rxjs';
import { finalize, retry, share, switchMap, takeUntil } from 'rxjs/operators';
import { getHttpInterceptorConfig } from '@app/core/interceptors/classes/http-interceptor-params';
import { RequestPriority } from '@app/core/interceptors/queue-requests-interceptor/enums/request-priority.enum';
import {
  MAX_CONCURRENT_REQUESTS,
  REQUEST_PRIORITY_SLOTS_CONFIG,
} from '@app/core/interceptors/queue-requests-interceptor/configs/request-priority-slots.config';
import { IRequestPrioritySlotsConfig } from '@app/core/interceptors/queue-requests-interceptor/interfaces/request-priority-slots-config.interface';
import { REQUEST_PRIORITY_ORDER_CONFIG } from '@app/core/interceptors/queue-requests-interceptor/configs/request-priority-order.config';
import { TOO_MANY_REQUESTS_RETRY_CONFIG } from '@app/core/interceptors/queue-requests-interceptor/configs/too-many-requests-retry.config';

/**
 * Caps concurrency at {@link MAX_CONCURRENT_REQUESTS}. Cancelling drops queued requests,
 * but never aborts in-flight ones — the backend processes them anyway.
 *
 * A freed slot goes to the queued request picked by {@link RequestPriority} (`priority` in
 * `HttpInterceptorParams`) and its {@link REQUEST_PRIORITY_SLOTS_CONFIG}, FIFO within the same priority.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpQueueService {
  private readonly cancelPendingRequests$ = new Subject<void>();

  // insertion order is the FIFO order
  private readonly pendingSlots = new Map<ReplaySubject<void>, RequestPriority>();
  private readonly activeSlots = new Map<ReplaySubject<void>, RequestPriority>();

  private isCancelling = false;

  public enqueue(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const interceptorConfig = getHttpInterceptorConfig(request);
    const cancellation$: Observable<void> = interceptorConfig?.toIgnoreQueueCancellation
      ? NEVER
      : this.cancelPendingRequests$;

    return defer(() => {
      if (interceptorConfig?.toCancelPendingRequests) {
        this.cancelPendingRequests();
      }

      const request$ = this.waitForSlotAndSend(request, next, cancellation$);

      if (interceptorConfig?.toIgnoreTooManyRequestsRetry) {
        return request$;
      }

      return request$.pipe(
        retry({
          count: TOO_MANY_REQUESTS_RETRY_CONFIG.maxRetries,
          delay: (error: unknown, retryCount: number) =>
            this.delayTooManyRequestsRetry(error, retryCount, cancellation$),
        })
      );
    });
  }

  private waitForSlotAndSend(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    cancellation$: Observable<void>
  ): Observable<HttpEvent<unknown>> {
    return defer(() => {
      const slot: ReplaySubject<void> = new ReplaySubject<void>(1);

      // Priority only orders the queue, nothing skips the limit: over it the backend answers 429 (see ISSUES.md M-13).
      this.pendingSlots.set(slot, getHttpInterceptorConfig(request)?.priority ?? RequestPriority.High);
      this.runQueue();

      return slot.pipe(
        takeUntil(cancellation$),
        switchMap(() => this.sendHoldingSlotUntilResponse(request, next, slot, cancellation$)),
        finalize(() => {
          if (this.pendingSlots.has(slot)) {
            this.releaseSlot(slot);
          }
        })
      );
    });
  }

  private sendHoldingSlotUntilResponse(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    slot: ReplaySubject<void>,
    cancellation$: Observable<void>
  ): Observable<HttpEvent<unknown>> {
    return next(request).pipe(
      finalize(() => this.releaseSlot(slot)),
      // Keeps the call alive when the caller unsubscribes.
      share({ resetOnRefCountZero: false }),
      takeUntil(cancellation$)
    );
  }

  /** Completes without a value when cancelled, which ends the request quietly. */
  private delayTooManyRequestsRetry(
    error: unknown,
    retryCount: number,
    cancellation$: Observable<void>
  ): Observable<unknown> {
    if (!(error instanceof HttpErrorResponse) || error.status !== HttpStatusCode.TooManyRequests) {
      return throwError(() => error);
    }

    // `retryCount` starts at 1: 1s, 2s, 4s, 8s, 16s.
    return timer(TOO_MANY_REQUESTS_RETRY_CONFIG.delayMs * 2 ** (retryCount - 1)).pipe(takeUntil(cancellation$));
  }

  private cancelPendingRequests(): void {
    this.isCancelling = true;
    this.cancelPendingRequests$.next();
    this.isCancelling = false;

    this.runQueue();
  }

  private runQueue(): void {
    if (this.isCancelling) {
      return;
    }

    while (this.activeSlots.size < MAX_CONCURRENT_REQUESTS) {
      const slot = this.pickNextSlot();

      // nothing waits, or every waiting priority holds its `maxSlots`
      if (!slot) {
        return;
      }

      this.activeSlots.set(slot, this.pendingSlots.get(slot)!);
      this.pendingSlots.delete(slot);

      slot.next();
      slot.complete();
    }
  }

  /** See {@link REQUEST_PRIORITY_SLOTS_CONFIG}: guaranteed slots first, then by priority. */
  private pickNextSlot(): ReplaySubject<void> | undefined {
    const activeCount = (priority: RequestPriority): number =>
      [...this.activeSlots.values()].filter(slotPriority => slotPriority === priority).length;

    const findPending = (priority: RequestPriority): ReplaySubject<void> | undefined =>
      [...this.pendingSlots].find(([, slotPriority]) => slotPriority === priority)?.[0];

    const findPendingBelow = (order: RequestPriority[], limit: (config: IRequestPrioritySlotsConfig) => number) =>
      order
        .filter(priority => activeCount(priority) < limit(REQUEST_PRIORITY_SLOTS_CONFIG[priority]))
        .map(findPending)
        .find(slot => !!slot);

    // guarantees go lowest priority first: a higher one wins every other pick anyway
    return (
      findPendingBelow([...REQUEST_PRIORITY_ORDER_CONFIG].reverse(), config => config.guaranteedSlots) ??
      findPendingBelow(REQUEST_PRIORITY_ORDER_CONFIG, config => config.maxSlots)
    );
  }

  private releaseSlot(slot: ReplaySubject<void>): void {
    this.pendingSlots.delete(slot);
    this.activeSlots.delete(slot);

    this.runQueue();
  }
}
